import React from "react";
import { toast } from "sonner";
import { Copy, Eye, KeyRound, Mail, Power } from "lucide-react";
import { api } from "../../lib/api";
import { reactivationChoices } from "../../lib/limoAccount";
import { accountChip, passwordChip } from "../../lib/limoGroupAccess";

/**
 * One participant's Logiscool access, inside the cohort's list (08.10.2026):
 * the account and password chips, when the login was sent, and the same four
 * actions as on the enrolment — see, reset, resend, reactivate.
 *
 * The parent holds which row is open, so access details are shown for one
 * participant at a time and vanish when another opens or the list unmounts.
 * They never go into a toast.
 */
const detail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const day = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ro-RO");
};

const SEND_REASONS = {
  credentials_unavailable: "Logiscool nu a returnat încă datele de acces. Încearcă din nou peste un minut.",
  password_changed_by_user: "Cursantul și-a schimbat singur parola. Resetează parola: cea nouă se trimite automat.",
  email_failed: "Emailul nu a putut fi trimis. Verifică setările Resend.",
};

const btn = "inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[10px] "
  + "font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-50";

export default function LimoAccessRow({ item, account, sandbox, free, busy, setBusy, open, setOpen, onChanged }) {
  const id = item.enrollment_id;
  const mine = open?.id === id ? open : null;
  const chip = accountChip(account);
  const pwd = passwordChip(account);
  const params = { sandbox };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const view = () => run(async () => {
    try {
      const r = await api.get(`/admin/enrollments/${id}/limo/credentials`, { params });
      if (r.data?.ok) setOpen({ id, kind: "creds", creds: { ...r.data, fresh: false } });
      else toast.error(r.data?.error === "student_not_found"
        ? "Logiscool nu găsește acest cursant." : "Nu am putut citi datele de acces.");
    } catch (err) {
      toast.error(detail(err, "Nu am putut citi datele de acces."));
    }
  });

  const reset = () => {
    if (account?.enabled === false
      && window.confirm(`Contul lui ${item.name} e dezactivat: cu o parolă nouă tot nu va putea intra. Îl reactivezi întâi?`)) {
      setOpen({ id, kind: "reactivate" });
      return;
    }
    if (!window.confirm(`Generezi o parolă nouă pentru ${item.name}? Cea veche nu va mai funcționa. `
      + (sandbox ? "Emailul îți vine ție, ca probă." : "Parola nouă se trimite automat pe email cursantului."))) return;
    run(async () => {
      try {
        const r = await api.post(`/admin/enrollments/${id}/limo/reset-password`, null, { params });
        if (!r.data?.ok) { toast.error(r.data?.error || "Resetarea a eșuat."); return; }
        setOpen({ id, kind: "creds", creds: { username: r.data.username, password: r.data.password, fresh: true } });
        const sent = r.data.access_email?.sent;
        (sent ? toast.success : toast.warning)(sent
          ? "Parolă nouă generată și trimisă pe email."
          : `Parolă nouă generată, dar emailul nu a plecat: ${SEND_REASONS[r.data.access_email?.reason] || "motiv necunoscut"}.`);
        await onChanged();
      } catch (err) {
        toast.error(detail(err, "Resetarea a eșuat."));
      }
    });
  };

  const resend = () => run(async () => {
    try {
      const r = await api.post(`/admin/enrollments/${id}/limo/send-access`, null, { params });
      if (r.data?.sent) {
        toast.success(sandbox ? "Trimis ție, ca probă." : `Datele de acces au plecat către ${item.name}.`);
        await onChanged();
      } else {
        toast.error(SEND_REASONS[r.data?.reason] || "Emailul nu a putut fi trimis.");
      }
    } catch (err) {
      toast.error(detail(err, "Emailul nu a putut fi trimis."));
    }
  });

  const reactivate = ({ readd }) => {
    const withdrawn = Boolean(account?.withdrawn);
    if (withdrawn && !window.confirm(reactivationChoices(account).withdrawnWarning)) return;
    run(async () => {
      try {
        const r = await api.post(`/admin/enrollments/${id}/limo/reactivate`, null,
          { params: { ...params, readd, confirm_withdrawn: withdrawn } });
        (r.data.readd_error ? toast.warning : toast.success)(`${item.name}: ${r.data.message}`);
        setOpen(null);
        await onChanged();
      } catch (err) {
        toast.error(detail(err, "Reactivarea nu a reușit."));
      }
    });
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiat.");
    } catch {
      toast.error("Nu am putut copia.");
    }
  };

  const choices = reactivationChoices(account, { free });

  return (
    <div className="mt-1 w-full" data-testid={`group-limo-access-${id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {chip && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.cls}`} title={chip.title}>{chip.label}</span>}
        {pwd && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${pwd.cls}`}>{pwd.label}</span>}
        <span className="text-[10px] text-[#6B7280]">
          {item.access_sent_at ? `date trimise ${day(item.access_sent_at)}` : "date netrimise"}
        </span>
        <span className="ml-auto flex flex-wrap gap-1">
          <button type="button" onClick={view} disabled={busy} className={btn} data-testid={`group-limo-view-${id}`}>
            <Eye size={11} /> Vezi
          </button>
          <button type="button" onClick={reset} disabled={busy} className={btn} data-testid={`group-limo-reset-${id}`}>
            <KeyRound size={11} /> Resetează
          </button>
          <button type="button" onClick={resend} disabled={busy} className={btn} data-testid={`group-limo-resend-${id}`}>
            <Mail size={11} /> {item.access_sent_at ? "Retrimite" : "Trimite"}
          </button>
          {account?.enabled === false && (
            <button type="button" onClick={() => setOpen({ id, kind: "reactivate" })} disabled={busy}
              className="inline-flex items-center gap-1 rounded-full bg-[#965EA7] px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
              data-testid={`group-limo-reactivate-${id}`}>
              <Power size={11} /> Reactivează
            </button>
          )}
        </span>
      </div>

      {mine?.kind === "creds" && (
        <div className="mt-1.5 rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2" data-testid={`group-limo-creds-${id}`}>
          <Cred label="Utilizator" value={mine.creds.username} onCopy={copy} />
          {mine.creds.password ? (
            <Cred label={mine.creds.fresh ? "Parolă nouă" : "Parolă"} value={mine.creds.password} onCopy={copy} mono />
          ) : (
            <div className="mt-0.5 text-[#8a6a1a]">Parola a fost schimbată de cursant, deci nu mai e cunoscută. Resetează-o dacă a uitat-o.</div>
          )}
          <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-[#6B7280]">
            <span>Citită acum de la Logiscool. Nu se salvează nicăieri.</span>
            <button type="button" onClick={() => setOpen(null)} className="underline">Ascunde</button>
          </div>
        </div>
      )}

      {mine?.kind === "reactivate" && (
        <div className="mt-1.5 rounded-lg border border-[#965EA7]/30 bg-[#F5F0F8] px-3 py-2" data-testid={`group-limo-reactivate-box-${id}`}>
          <div className="font-semibold text-[#373A45]">Reactivezi contul Logiscool al lui {item.name}?</div>
          <div className="text-[#6B7280]">Va putea intra din nou cu aceleași date; licențele lui se reiau.</div>
          {choices.withdrawnWarning && <div className="mt-1 font-semibold text-[#9b1f2c]">{choices.withdrawnWarning}</div>}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {choices.readdLabel && (
              <button type="button" onClick={() => reactivate({ readd: true })} disabled={busy || !choices.canReadd}
                className="rounded-full bg-[#965EA7] px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50">
                {choices.readdLabel}
              </button>
            )}
            <button type="button" onClick={() => reactivate({ readd: false })} disabled={busy}
              className={`rounded-full px-2.5 py-1 text-[10px] font-semibold disabled:opacity-50 ${choices.readdLabel
                ? "border border-[#965EA7]/40 bg-white text-[#6B3F7A] hover:bg-[#F5F0F8]"
                : "bg-[#965EA7] text-white hover:bg-[#7A4986]"}`}>
              {choices.readdLabel ? "Doar reactivează contul" : "Reactivează contul"}
            </button>
            <button type="button" onClick={() => setOpen(null)} disabled={busy}
              className="px-2 py-1 text-[10px] font-semibold text-[#6B7280] hover:text-[#373A45]">
              Renunță
            </button>
          </div>
          {choices.readdNote && <div className="mt-1 text-[10px] text-[#6B7280]">{choices.readdNote}</div>}
        </div>
      )}
    </div>
  );
}

function Cred({ label, value, onCopy, mono }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className={`text-[#373A45] ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
        {value && (
          <button type="button" onClick={() => onCopy(value)} className="text-[#6B7280] hover:text-[#373A45]" title="Copiază">
            <Copy size={11} />
          </button>
        )}
      </span>
    </div>
  );
}
